import React, { Suspense, useRef, useEffect } from 'react';
import { Canvas } from '@react-three/fiber';
import CanvasLoader from './components/CanvasLoader';
import { OrbitControls } from '@react-three/drei';
import Me from './components/Me';
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { TextPlugin } from "gsap/TextPlugin";

gsap.registerPlugin(ScrollTrigger, TextPlugin);

const About = () => {

  const sectionRef = useRef(null);
  const textRefs = useRef([]);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Kill any existing ScrollTriggers for this section
      ScrollTrigger.getAll().forEach((st) => st.kill());

      // Set initial state
      textRefs.current.forEach((ref) => {
        gsap.set(ref, { opacity: 0, y: 30 });
      });

      textRefs.current.forEach((ref, index) => {
        ScrollTrigger.create({
          trigger: ref,
          start: "top 85%",
          end: "top 80%",
          markers: false,
          onEnter: () => {
            gsap.to(ref, {
              opacity: 1,
              y: 0,
              duration: 1, // Slower animation
              ease: "power2.out",
              clearProps: "transform",
              scrollTrigger: {
                trigger: ref,
                start: "top 85%",
                toggleActions: "play none none reverse",
              },
            });
          },
          onLeaveBack: () => {
            gsap.to(ref, {
              opacity: 0,
              y: 30,
              duration: 0.5,
              ease: "power2.in",
            });
          },
          once: false,
          preventOverlaps: true,
          fastScrollEnd: true,
        });
      });

      // Add a main section trigger for better control
      ScrollTrigger.create({
        trigger: sectionRef.current,
        start: "top 90%",
        end: "bottom 10%",
        markers: false,
        onLeave: () => {
          textRefs.current.forEach((ref) => {
            gsap.set(ref, { opacity: 0, y: 30 });
          });
        },
        onEnterBack: () => {
          textRefs.current.forEach((ref, index) => {
            gsap.to(ref, {
              opacity: 1,
              y: 0,
              duration: 1,
              delay: index * 0.2,
              ease: "power2.out",
            });
          });
        },
      });
    }, sectionRef);

    return () => {
      ctx.revert();
    };
  }, []);

  return (
    <div ref={sectionRef} id='about' className='flex flex-col items-center justify-center lg:my-10 w-full'>
      <h1 className='lg:mt-32 mt-16 mb-10 text-3xl lg:text-6xl'>About Me</h1>
      <div className="flex items-center flex-col lg:flex-row justify-between gap-20 h-full w-full lg:px-60">
        <div className="border-[0.2px] shadow-2xl border-white lg:h-[85vh] justify-between">
          <Canvas>
            <ambientLight intensity={7} />
            <spotLight position={[10, 10, 10]} angle={0.15} penumbra={1} />
            <directionalLight position={[10, 10, 10]} intensity={1} />
            <OrbitControls enableZoom={false} maxPolarAngle={Math.PI / 2} />
            <Suspense fallback={<CanvasLoader />} />
            <Me position-y={-2.7} scale={3} />
          </Canvas>
        </div>
        <div className='text-xl lg:max-w-[24rem] text-left flex flex-col gap-14 mx-12 py-6'>

          <div ref={(el) => (textRefs.current[1] = el)} className='flex flex-col'>
            <h1 className='text-3xl mb-3'>
              Hello🙋🏾‍♂️✨
            </h1>
            <p className='text-[15px]'>I'm Ajayi Samuel, a versatile developer with expertise in React, Next.js, Solidity, and cloud engineering. I’m passionate about building scalable applications and exploring the intersection of web2 and web3 technologies.</p>
          </div>

          <div ref={(el) => (textRefs.current[2] = el)} className='flex flex-col'>
            <h1 className='text-3xl mb-3'>
              My Journey🛣
            </h1>
            <p className='text-[15px]'>I began my development journey in 2022, going into JavaScript frameworks and blockchain technologies. I expanded into smart contract development and later, google cloud infrastructure, creating highly performing web applications.</p>
          </div>

          <div ref={(el) => (textRefs.current[3] = el)} className='flex flex-col'>
            <h1 className='text-3xl mb-3'>
              Other skills📝✍🏾
            </h1>
            <p className='text-[15px]'>I am also interested in technical writing. Using my expertise and know how in the English Language, I am confident I will be able to write properly about any given topic within the web2/web3 ecosystem and google cloud platform and its resources</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default About;